/* ─────────────────────────────────────────────────────────────
   ORA Indie — Smooth Scroll  (Lenis + GSAP ScrollTrigger)
   Loaded after theme.js via theme.liquid
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── 1. Respect prefers-reduced-motion ── */
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return; // native scroll for accessibility

  /* ── 1b. Disable smooth scroll on touch / small screens ──
     Lenis (driven by GSAP ticker with autoRaf:false) can miscalculate
     scroll height on mobile, cutting off the footer. Use native scroll. */
  const isTouchOrMobile = window.matchMedia('(max-width: 900px)').matches
    || ('ontouchstart' in window)
    || (navigator.maxTouchPoints > 0);
  if (isTouchOrMobile) return; // native scroll on mobile — footer stays reachable

  /* ── 2. Wait for Lenis + GSAP to be available ── */
  function waitForDeps(cb) {
    if (typeof Lenis !== 'undefined' && typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      cb();
    } else {
      requestAnimationFrame(() => waitForDeps(cb));
    }
  }

  waitForDeps(init);

  /* ── 3. Core init ── */
  function init() {

    /* Create Lenis instance
       - duration: subtle, not sluggish — 1.1s feels premium without being slow
       - easing: custom cubic that starts fast and trails off elegantly
       - smoothWheel: true on desktop, false on touch devices (native momentum is better)
       - touchMultiplier / wheelMultiplier: keep close to 1 to avoid overshooting
    */
    const lenis = new Lenis({
      duration: 1.1,
      easing: function (t) {
        // Custom ease: cubic-bezier-like curve — fast start, graceful end
        return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      },
      smoothWheel: true,
      touchMultiplier: 1.0,
      wheelMultiplier: 0.9,
      infinite: false,
      autoRaf: false, // we drive the RAF ourselves for GSAP sync
    });

    /* ── 4. Sync Lenis → GSAP ScrollTrigger ── */
    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add(function (time) {
      lenis.raf(time * 1000); // GSAP time is in seconds, Lenis expects ms
    });

    gsap.ticker.lagSmoothing(0); // prevent GSAP from compensating for tab-switch lag

    /* ── 5. Patch window.scrollY for theme.js header logic ──
       theme.js reads window.scrollY directly in its scroll listener.
       Lenis scrolls the actual document, so window.scrollY IS updated
       correctly — no patch needed. But we do need to ensure Lenis
       doesn't hijack the native 'scroll' event. It doesn't — it fires
       its own 'scroll' event on the lenis instance AND dispatches a
       native scroll event, so theme.js continues to work unchanged.
    */

    /* ── 6. Anchor link handling ──
       Intercept clicks on in-page anchor links and let Lenis
       scroll smoothly to the target instead of jumping.
    */
    document.addEventListener('click', function (e) {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');

      // Only handle pure hash links (e.g. #section-id) or same-page hash
      if (!href || !href.startsWith('#')) return;
      const id = href.slice(1);
      if (!id) return; // bare "#" — ignore

      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      lenis.scrollTo(target, {
        offset: -(parseInt(getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height')) || 0),
        duration: 1.0,
      });
    });

    /* ── 7. Pause Lenis when overlays lock body scroll ──
       theme.js sets document.body.style.overflow = 'hidden' when
       cart drawer, search overlay, mobile menu, etc. open.
       We observe this and stop/start Lenis accordingly so it
       doesn't fight with locked scroll states.
    */
    const bodyObserver = new MutationObserver(function () {
      if (document.body.style.overflow === 'hidden') {
        lenis.stop();
      } else {
        lenis.start();
      }
    });

    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    /* ── 8. Expose instance globally for other scripts ── */
    window.__oraLenis = lenis;

    /* ── 9. ScrollTrigger default scroller ──
       Tell ScrollTrigger to use the window as scroller (Lenis
       scrolls the window, not a wrapper element).
    */
    ScrollTrigger.defaults({ scroller: window });

    /* ── 10. Subtle scroll-reveal with GSAP + ScrollTrigger ──
       Replaces the IntersectionObserver approach in theme.js
       for elements that already have .js-reveal.
       We leave the IntersectionObserver in theme.js untouched
       as a fallback — elements will get .is-visible from whichever
       fires first. GSAP just adds a smoother stagger.
    */
    document.querySelectorAll('.js-reveal').forEach(function (el) {
      const delay = (parseInt(el.dataset.revealDelay) || 0) / 1000; // convert ms → s

      gsap.fromTo(el,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          delay: delay,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
            once: true,
          },
          onStart: function () {
            el.classList.add('is-visible'); // keep class in sync
          },
        }
      );
    });

  } // end init()

})();
